import { PageCanvas } from "@/components/page-canvas";
import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Errors } from "../requests/controls";
import { locationJobPaymentPostingsPath } from "@/routes";

export default function PaymentForm({
  job_id,
  ledger_context = {},
  revision,
  entry_key,
  currency,
  occurred_at,
  time_zone,
  financial,
  advance_request,
}: {
  job_id: string;
  ledger_context?: { page?: number; requests_page?: number };
  revision: string;
  entry_key: string;
  currency: string;
  occurred_at: string;
  time_zone: string;
  financial: boolean;
  advance_request: { id: string; reason: string } | null;
}) {
  const { auth, errors } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const path = locationJobPaymentPostingsPath(
    auth.location.id,
    job_id,
    ledger_context,
  );
  const choices = financial
    ? ["payment", "advance", "refund", "adjustment"]
    : ["payment", "advance", "adjustment"];
  const field = (name: string, value?: string) => (
    <div className="space-y-2">
      <Label htmlFor={`payment-${name}`}>
        {t(`payments.${name}`)}
        {name === "amount" ? ` (${currency})` : ""}
      </Label>
      <Input
        id={`payment-${name}`}
        name={`payment_posting[${name}]`}
        defaultValue={value}
        required
        aria-invalid={!!errors?.[name]}
        aria-describedby={errors?.[name] ? `payment-${name}-error` : undefined}
        maxLength={name === "amount" ? 25 : 500}
        inputMode={name === "amount" ? "decimal" : undefined}
      />
      {errors?.[name] && (
        <p id={`payment-${name}-error`} className="text-sm text-destructive">
          {String(errors[name])}
        </p>
      )}
    </div>
  );
  return (
    <JobWorkspace
      primaryAction={false}
      breadcrumbs={[{ title: t("payments.title"), href: path }]}
    >
      <Head title={t("payments.record")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">{t("payments.record")}</h2>
        <p className="text-muted-foreground text-sm">
          {t("payments.external_help")}
        </p>
        {advance_request && (
          <p role="status">
            {t("payments.selected_request")}: {advance_request.reason}
          </p>
        )}
        <Form action={path} method="post">
          {({ processing }) => (
            <div className="space-y-4">
              <Errors
                targets={{
                  amount: "payment-amount",
                  reference: "payment-reference",
                  reason: "payment-reason",
                  occurred_at: "payment-occurred_at",
                  method: "payment-method",
                  kind: "payment-kind",
                }}
              />
              {Object.entries(ledger_context).map(([key, value]) => (
                <input
                  key={key}
                  type="hidden"
                  name={`ledger[${key}]`}
                  value={value}
                />
              ))}
              <input
                type="hidden"
                name="payment_posting[revision]"
                value={revision}
              />
              <input
                type="hidden"
                name="payment_posting[entry_key]"
                value={entry_key}
              />
              <input
                type="hidden"
                name="payment_posting[currency]"
                value={currency}
              />
              {advance_request && (
                <input
                  type="hidden"
                  name="payment_posting[advance_request_id]"
                  value={advance_request.id}
                />
              )}
              <div className="space-y-2">
                <Label htmlFor="payment-kind">{t("payments.kind")}</Label>
                <Select
                  name="payment_posting[kind]"
                  defaultValue={advance_request ? "advance" : "payment"}
                  items={Object.fromEntries(
                    choices.map((kind) => [kind, t(`payments.kinds.${kind}`)]),
                  )}
                >
                  <SelectTrigger id="payment-kind" autoFocus>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {choices.map((kind) => (
                      <SelectItem key={kind} value={kind}>
                        {t(`payments.kinds.${kind}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {field("amount")}
              <p className="text-muted-foreground text-sm">
                {t("payments.amount_help")}
              </p>
              <div className="space-y-2">
                <Label htmlFor="payment-method">{t("payments.method")}</Label>
                <Select
                  name="payment_posting[method]"
                  defaultValue="cash"
                  items={Object.fromEntries(
                    ["cash", "bank_transfer", "card", "other"].map((method) => [
                      method,
                      t(`payments.methods.${method}`),
                    ]),
                  )}
                >
                  <SelectTrigger id="payment-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["cash", "bank_transfer", "card", "other"].map(
                      (method) => (
                        <SelectItem key={method} value={method}>
                          {t(`payments.methods.${method}`)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>
              {field("reference")}
              {field("occurred_at", occurred_at)}
              <p className="text-muted-foreground text-sm">
                {t("payments.time_help", { zone: time_zone })}
              </p>
              <div className="space-y-2">
                <Label htmlFor="payment-reason">{t("payments.reason")}</Label>
                <Textarea
                  id="payment-reason"
                  name="payment_posting[reason]"
                  required
                  maxLength={500}
                  aria-invalid={!!errors?.reason}
                />
                {errors?.reason && (
                  <p className="text-sm text-destructive">
                    {String(errors.reason)}
                  </p>
                )}
              </div>
              <Button type="submit" disabled={processing}>
                {t("payments.record")}
              </Button>
            </div>
          )}
        </Form>
        <Link className="block underline" href={path}>
          {t("inventory.cancel")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
